/**
 * JourneyFlowBuilder — the journey-flow canvas + inspector, and the ONE
 * modeler: queue, appointment and event journeys all render here. Promoted out
 * of screens/queues on 2026-08-25 (the Queues section is going away, this is
 * not); the queue-side shim was deleted with the queue wizard on 2026-08-26.
 *
 * Everything structural is shared — step kinds, tree helpers, fb-layout's
 * auto-layout, node chrome, the inspector, the library pickers. Only the WORDS
 * vary, and they arrive in the vocab descriptor: the lifecycle moments that can
 * be placed on the canvas, the conditions a decision tests, the outcomes a flow
 * ends on, and what a wait is measured from. vocab defaults to the queue set,
 * so the queue flows page behaves exactly as before.
 *
 * Was the SIBLING of EventFlowBuilder (same visual language) over jf-model.
 * Reuses fb-layout's pure auto-layout engine (step shapes are layout-
 * compatible; the extra "stage" kind renders as a generic-height node).
 *
 * Controlled: props.flow ({ trigger, steps }) + props.onFlow(next).
 * Message steps pull from the SAME Messaging library as event flows
 * (useMessaging — live messages only); surveys from the Surveys library.
 */
import { useState, useMemo, useRef, useEffect, type JSX, type ReactNode, type CSSProperties, type MouseEvent, type DragEvent as RDragEvent } from "react";
import { MediaRow, alpha, Btn, Dropdown, Icon, IconBtn, T } from "@v0y4ge/jrni-ui";
import { useNav } from "../../../shell/router";
import { fbLayout, type NodeRect } from "../../builder/fb-layout";
import { fbNodeHeight } from "../../builder/fb-model";
import type { FBFlow, FBStep } from "../../builder/fb-model";
import { CH_COLORS } from "../../builder/EventFlowBuilder";
import { useMessaging } from "../../messaging/useMessaging";
import { useSurveys } from "../../surveys/SurveyEditorPage";
import {
  QF_WAIT_UNITS, QF_NODE_W, QF_VOCAB_QUEUE, QF_FLOW_TRIGGER_IN, type QFVocab, type QFPagesAdapter, type QFPageEntry,
  qfFindStep, qfEdit, qfRemove, qfInsert, qfMove, qfCanMove, qfWaitLabel, qfMigrate, qfFlatten,
  qfMkPage, qfMkComms, qfMkWait, qfMkDecision, qfMkSurvey, qfMkExit, qfMkTrigger,
  fpLabel, fpStyle, fpAddPath, fpRemovePath, FP_MAX_PATHS, FP_CUSTOM,
  type QFFlow, type QFStep, type QInsertDesc,
} from "./jf-model";
import { NumberField } from "../../../lib/NumberField";

import { useT } from "@v0y4ge/i18n-runtime";
const ACCENT = T.p1;
const SURVEY_TINT = "#E6F5F1", SURVEY_COLOR = "#0E8A6A";

function qfSelect(value: string, onChange: (v: string) => void, opts: (string | { value: string; label: string })[]) {
  return (
    <Dropdown
      options={opts.map((o) => (typeof o === "string" ? { value: o, label: o } : o))}
      value={value || null}
      onChange={onChange}
      style={{ gap: 0, width: "100%" }}
    />
  );
}
function QfField({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: T.n1, marginBottom: 5 }}>{label}</div>
      {children}
      {hint ? <div style={{ fontSize: 11, color: T.soft, marginTop: 5 }}>{hint}</div> : null}
    </div>
  );
}
function QfHead({ icon, tint, color, title, sub }: { icon: ReactNode; tint: string; color: string; title: string; sub: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
      <span style={{ width: 34, height: 34, borderRadius: 9, background: tint, color, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{icon}</span>
      <div><div style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{title}</div><div style={{ fontSize: 11.5, color: T.soft }}>{sub}</div></div>
    </div>
  );
}
const chooseBtn: CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, width: "100%", padding: "10px 12px", border: `1px solid ${T.n4}`, borderRadius: 8, background: T.white, cursor: "pointer", fontFamily: T.font };
const pickRowStyle: CSSProperties = { display: "flex", alignItems: "center", gap: 11, width: "100%", padding: 10, border: "none", background: "transparent", borderRadius: 9, cursor: "pointer", fontFamily: T.font };

export function JourneyFlowBuilder({ flow: rawFlow, onFlow, readOnly = false, height = "100%", heroImage, vocab = QF_VOCAB_QUEUE, pages }:
  { flow: QFFlow; onFlow: (next: QFFlow) => void; readOnly?: boolean; height?: number | string; heroImage?: string; vocab?: QFVocab; pages: QFPagesAdapter }) {
  const PMini = pages.Mini as (p: { page: string; cfg: Record<string, unknown>; scale?: number; heroImage?: string }) => JSX.Element;
  const PEditor = pages.Editor as ((p: { entries: QFPageEntry[]; startStepId: string; heroImage?: string; onDone: (c: Record<string, Record<string, unknown>>) => void; onCancel: () => void }) => JSX.Element) | undefined;
  const t = useT();
  const nav = useNav();
  // Migrate legacy stage-based flows to the page-based journey model on the way in.
  const flow = useMemo(() => qfMigrate(rawFlow), [JSON.stringify(rawFlow)]);
  const messagesStore = useMessaging();
  const msgLibrary = messagesStore.all().map((m) => ({ id: m.id, name: m.name, channels: m.channels }));
  // Live (approved) messages only, and ONLY SMS/WhatsApp — the valid channels
  // for queuing (operator direction 2026-07-21). Email/push templates are
  // filtered out of the queue-flow picker.
  const msgPickable = messagesStore.all().filter((m) => m.status === "live" && (m.channels ?? []).some((c: string) => c === "sms" || c === "whatsapp")).map((m) => ({ id: m.id, name: m.name, channels: m.channels.filter((c: string) => c === "sms" || c === "whatsapp") }));
  const { surveys } = useSurveys();
  const svLibrary = surveys.map((s) => ({ id: s.id, name: s.name, count: (s.items || []).length }));

  const [selectedId, setSelectedId] = useState("");
  const [picker, setPicker] = useState<{ x: number; y: number; desc: QInsertDesc } | null>(null);
  const [msgPick, setMsgPick] = useState<{ stepId: string } | null>(null);
  const [svPick, setSvPick] = useState<{ stepId: string } | null>(null);
  const [pageEdit, setPageEdit] = useState<string | null>(null); // page-step id → the host's page editor is open
  const [zoom, setZoom] = useState(1);
  const scrollRef = useRef<HTMLDivElement>(null);

  const commit = (next: QFFlow) => onFlow(next);
  const setSteps = (fn: (s: QFStep[]) => QFStep[]) => commit({ ...flow, steps: fn(flow.steps) });

  // Dynamic node spacing: node heights vary with content (page minis, comms
  // channel chips…), so the canvas MEASURES every node's real height
  // (ResizeObserver) and re-lays out with the measured values — no overlaps
  // between nodes or ⊕ slots.
  const measuredRef = useRef<Record<string, number>>({});
  const [mTick, setMTick] = useState(0);
  const roRef = useRef<ResizeObserver | null>(null);
  if (!roRef.current && typeof ResizeObserver !== "undefined") {
    roRef.current = new ResizeObserver((entries) => {
      let changed = false;
      entries.forEach((en) => {
        const el = en.target as HTMLElement;
        const id = el.dataset.qfid;
        if (!id || !el.isConnected) return;
        const h = Math.ceil(el.offsetHeight);
        if (h > 0 && Math.abs((measuredRef.current[id] ?? 0) - h) > 2) { measuredRef.current[id] = h; changed = true; }
      });
      if (changed) setMTick((t) => t + 1);
    });
  }
  useEffect(() => () => roRef.current?.disconnect(), []);
  const measureRef = (id: string) => (el: HTMLDivElement | null) => { if (el) { el.dataset.qfid = id; roRef.current?.observe(el); } };

  // No trigger node (operator direction 2026-07-21): the flow starts with the
  // first customer screen — just pages and actions.
  const layout = useMemo(() => fbLayout(flow as unknown as FBFlow, {
    noTrigger: true,
    nodeHeight: (s: FBStep) => {
      const id = (s as { id?: string }).id ?? "";
      const isPage = (s as { kind?: string }).kind === "page";
      const base = isPage ? 120 : fbNodeHeight(s);
      return Math.max(base, measuredRef.current[id] ?? (isPage ? fbNodeHeight(null) : 0));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [JSON.stringify(flow), mTick]);

  useEffect(() => {
    const el = scrollRef.current; if (!el) return;
    const contentW = layout.width * zoom + 80;
    const apply = () => { const e = scrollRef.current; if (e) e.scrollLeft = Math.max(0, (contentW - e.clientWidth) / 2); };
    requestAnimationFrame(apply); const t = setTimeout(apply, 120); return () => clearTimeout(t);
  }, [layout.width, zoom]);

  const sel = selectedId ? qfFindStep(flow.steps, selectedId) : null;

  const updateStep = (id: string, patch: Partial<QFStep>) => setSteps((s) => qfEdit(s, id, (x) => ({ ...x, ...patch }) as QFStep));
  const deleteStep = (id: string) => { setSteps((s) => qfRemove(s, id)); setSelectedId("trigger"); };
  /* The host owns page editing: either a modal Editor the builder renders, or
     a navigation callback (the Journey Designer's own Pages view). */
  const openPage = (stepId: string, page: string) => {
    if (PEditor) { setPageEdit(stepId); return; }
    pages.onOpenPage?.(stepId, page);
  };
  /* Dragging a node to another position, including onto another path (operator,
     2026-09-02). The canvas already has drop targets: `layout.slots` are the +
     buttons, each carrying the `{parentId, pathKey, index}` an insert needs — so
     a move is the same target list an add already uses, and there is nothing new
     for an author to learn.
     A custom payload type, not text/plain, so a stray text drag cannot reorder
     a flow. */
  const QF_DND = "application/x-qf-step";
  const [dragId, setDragId] = useState<string | null>(null);
  const moveTo = (desc: QInsertDesc, id: string) => {
    const next = qfMove(flow.steps, id, desc);
    setDragId(null);
    /* Reference equality means the move was refused or was a no-op. Committing
       anyway would put an identical tree on the undo stack, so ctrl-Z after a
       fumbled drag would appear to do nothing. */
    if (next === flow.steps) return;
    commit({ ...flow, steps: next });
    setSelectedId(id);
  };
  const insertAt = (desc: QInsertDesc, step: QFStep, selectAfter?: boolean) => { commit({ ...flow, steps: qfInsert(flow.steps, desc, step) }); setPicker(null); if (selectAfter) setSelectedId(step.id); };

  const stats = useMemo(() => {
    let steps = 0, messages = 0, pages = 0, decisions = 0;
    const walk = (arr: QFStep[]) => arr.forEach((s) => { steps++; if (s.kind === "comms" || s.kind === "survey") messages++; else if (s.kind === "page" || s.kind === "stage") pages++; else if (s.kind === "decision") { decisions++; (s.paths ?? []).forEach((p) => walk(p.steps ?? [])); } });
    walk(flow.steps);
    return { steps, messages, pages, decisions };
  }, [JSON.stringify(flow)]);

  const usedPages = useMemo(() => {
    const set = new Set<string>();
    const walk = (arr: QFStep[]) => arr.forEach((s) => { if (s.kind === "page") set.add(s.page); else if (s.kind === "decision") { (s.paths ?? []).forEach((p) => walk(p.steps ?? [])); } });
    walk(flow.steps);
    return set;
  }, [JSON.stringify(flow)]);

    /* Props every node spreads to become draggable. On the box rather than on a
     handle: the node IS the thing being moved, and a 12px grip on a canvas you
     also pan is a smaller target than the node itself.
     Pages, messages, waits, surveys, decisions and stages all move. The EXIT
     node does not — it is the end of a path, and a path's end is where it is
     because of the path, not because of an ordering choice. */
  const dragProps = (id: string, movable: boolean) => (readOnly || !movable ? {} : {
    draggable: true,
    onDragStart: (e: RDragEvent<HTMLElement>) => {
      e.stopPropagation();
      e.dataTransfer.setData(QF_DND, id);
      e.dataTransfer.effectAllowed = "move";
      setDragId(id);
    },
    onDragEnd: () => setDragId(null),
  });

function nodeBox(n: NodeRect, on: boolean): CSSProperties {
    const isPage = (n.step as { kind?: string } | undefined)?.kind === "page";
    return {
      // minHeight stays LOW so the DOM height reflects real content — the
      // ResizeObserver feeds it back into the layout (dynamic spacing).
      position: "absolute", left: n.x, top: n.y, width: QF_NODE_W, minHeight: isPage ? 120 : 44, boxSizing: "border-box",
      background: T.white, border: `1.5px solid ${on ? ACCENT : T.border}`, borderRadius: 12, padding: 14,
      boxShadow: on ? `0 0 0 2px ${alpha(ACCENT, "15")}, 0 10px 22px -12px rgba(16,24,40,.28)` : "0 1px 2px rgba(16,24,40,.05), 0 8px 18px -14px rgba(16,24,40,.22)",
      cursor: "pointer", zIndex: 1,
    };
  }

  const renderNode = (n: NodeRect) => {
    if (n.trigger) return null; // no trigger node — the flow starts at the first screen
    const s = n.step as unknown as QFStep; const on = selectedId === s.id;
    const click = (e: MouseEvent) => { e.stopPropagation(); setSelectedId(s.id); };
    if (s.kind === "qtrigger") {
      const trig = QF_FLOW_TRIGGER_IN(vocab, s.event);
      return (
        <div key={s.id} ref={measureRef(s.id)} onClick={click} data-testid="qflow-trigger-node" {...dragProps(s.id, true)}
          style={{ ...nodeBox(n, on), background: "#14131F", color: "#fff", borderColor: on ? ACCENT : "#14131F" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <span style={{ width: 30, height: 30, borderRadius: 8, background: "rgba(255,255,255,.14)", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.users size={15}/></span>
            <div style={{ minWidth: 0 }}><div style={{ fontSize: 11, opacity: .7, fontWeight: 600, textTransform: "uppercase", letterSpacing: .05 }}>{t('queues.flowBuilder.text')}</div><div style={{ fontSize: 13.5, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t(trig.labelKey, trig.label)}</div></div>
          </div>
        </div>
      );
    }
    if (s.kind === "page") {
      const meta = pages.meta(s.page);
      // Clicking a page opens its editor (PM: "configure a page by clicking on it").
      return (
        <div key={s.id} ref={measureRef(s.id)} {...dragProps(s.id, true)} onClick={(e) => { e.stopPropagation(); setSelectedId(s.id); if (!readOnly) openPage(s.id, s.page); }}
          style={{ ...nodeBox(n, on), display: "flex", flexDirection: "column", gap: 8 }} data-testid="qflow-page-node">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 24, height: 24, borderRadius: 6, background: meta.tint, color: meta.color, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.phone size={12}/></span>
            <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{meta.labelKey ? t(meta.labelKey, meta.label) : meta.label}</span>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: meta.color, background: meta.tint, borderRadius: 999, padding: "2px 7px", flexShrink: 0 }}>{t('queues.flowBuilder.text2')}</span>
            {!readOnly ? (
              /* ui-raw: delete affordance pinned to a flow node — no flow-builder family exists (known gap) */
              <button onClick={(e) => { e.stopPropagation(); deleteStep(s.id); }} title={t('queues.flowBuilder.title')} aria-label={/* TODO(i18n): replace template-literal with t('queues.flowBuilder.ariaLabel', { ... }) */`Remove ${meta.label} page`} data-testid="qflow-delete-page-btn"
                style={{ border: "none", background: "transparent", color: T.n3, cursor: "pointer", display: "inline-flex", padding: 2, flexShrink: 0 }}><Icon.x size={13}/></button>
            ) : null}
          </div>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <PMini page={s.page} cfg={s.cfg} heroImage={heroImage} />
          </div>
        </div>
      );
    }
    if (s.kind === "comms") {
      const m = msgLibrary.find((x) => x.id === s.messageId) ?? null;
      return (
        <div key={s.id} ref={measureRef(s.id)} onClick={click} {...dragProps(s.id, true)} style={nodeBox(n, on)}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <span style={{ width: 26, height: 26, borderRadius: 7, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.msg size={14}/></span>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: T.n1, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m ? m.name : t('queues.flowBuilder.text3')}</span>
          </div>
          {m ? <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{m.channels.map((c) => <span key={c} style={{ fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 999, background: CH_COLORS[c] ?? T.n5, color: "#fff" }}>{c}</span>)}</div>
             : <div style={{ fontSize: 12, color: T.soft }}>{t('queues.flowBuilder.text4')}</div>}
          <div style={{ marginTop: 8, fontSize: 11, color: T.soft }}>{s.sendWhen || "Immediately"}</div>
        </div>
      );
    }
    if (s.kind === "wait") return (
      <div key={s.id} ref={measureRef(s.id)} onClick={click} {...dragProps(s.id, true)} style={nodeBox(n, on)}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 26, height: 26, borderRadius: 7, background: "#EFF1F4", color: "#7C8597", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon.clock size={14}/></span>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: T.n1 }}>{qfWaitLabel(s, vocab)}</span>
        </div>
      </div>
    );
    if (s.kind === "decision") return (
      <div key={s.id} ref={measureRef(s.id)} onClick={click} {...dragProps(s.id, true)} style={{ ...nodeBox(n, on), background: "#FAF7EE", borderColor: on ? ACCENT : "#EBDFC4" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 26, height: 26, borderRadius: 7, background: "#FAF1DE", color: "#C8861E", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.network size={14}/></span>
          <span style={{ fontSize: 13, fontWeight: 600, color: T.n1, minWidth: 0 }}>{t('queues.flowBuilder.text5')}{" "}{s.condition.charAt(0).toLowerCase() + s.condition.slice(1)}</span>
        </div>
        {(s.paths?.length ?? 0) > 2 ? (
          <div style={{ display: "flex", gap: 4, marginTop: 8, flexWrap: "wrap" }}>
            {s.paths.map((p, i) => (
              <span key={p.key} style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 999, background: fpStyle(i).tint, color: fpStyle(i).color }}>{fpLabel(p)}</span>
            ))}
          </div>
        ) : null}
      </div>
    );
    if (s.kind === "survey") {
      const sv = svLibrary.find((x) => x.id === s.surveyId) ?? null;
      return (
        <div key={s.id} ref={measureRef(s.id)} onClick={click} {...dragProps(s.id, true)} style={{ ...nodeBox(n, on), background: "#F0F9F6", borderColor: on ? ACCENT : "#C9E8DF" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <span style={{ width: 26, height: 26, borderRadius: 7, background: SURVEY_TINT, color: SURVEY_COLOR, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.csv size={14}/></span>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: T.n1, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sv ? sv.name : t('queues.flowBuilder.text6')}</span>
          </div>
          <div style={{ fontSize: 12, color: T.soft }}>{sv ? /* TODO(i18n): replace template-literal with t('queues.flowBuilder.text7', { ... }) */`CSAT survey · ${sv.count} question${sv.count === 1 ? "" : "s"}` : t('queues.flowBuilder.text8')}</div>
          <div style={{ marginTop: 8, fontSize: 11, color: T.soft }}>{s.sendWhen || "Immediately"}</div>
        </div>
      );
    }
    return (
      <div key={s.id} ref={measureRef(s.id)} onClick={click} style={{ ...nodeBox(n, on), background: "#F1F4F8", borderColor: on ? ACCENT : "#DBE2EA" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 26, height: 26, borderRadius: 7, background: "#E7ECF2", color: "#475569", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.check size={14}/></span>
          <div><div style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{(s as any).outcome || "End flow"}</div><div style={{ fontSize: 11, color: T.soft }}>{(s as any).note || "Customer leaves the flow"}</div></div>
        </div>
      </div>
    );
  };

  const nextPage: string = pages.order.filter((p) => !usedPages.has(p))[0] ?? pages.order[0];
  const pageItems = (desc: QInsertDesc) => pages.order.map((p) => {
    const pm = pages.meta(p);
    return {
      label: pm.labelKey ? t(pm.labelKey, pm.label) : pm.label, hint: pm.sub, accent: pm.color, bg: pm.tint,
      icon: <Icon.phone size={15}/>, suggested: p === nextPage,
      onPick: () => { const step = qfMkPage(p, pages.defaultCfg(p)); insertAt(desc, step, true); openPage(step.id, p); },
    };
  });
  const insertItems = (desc: QInsertDesc) => [
    { label: "Send a message",  hint: "Pull from your Messaging library",  accent: T.p1, bg: T.p4, icon: <Icon.msg size={16}/>, onPick: () => { const step = qfMkComms(); insertAt(desc, step, true); setMsgPick({ stepId: step.id }); } },
    { label: "CSAT survey",     hint: "Pull from your Surveys library",    accent: SURVEY_COLOR, bg: SURVEY_TINT, icon: <Icon.csv size={16}/>, onPick: () => { const step = qfMkSurvey(); insertAt(desc, step, true); setSvPick({ stepId: step.id }); } },
    { label: "Wait / delay",    hint: "Pause before the next step",        accent: "#7C8597", bg: "#EFF1F4", icon: <Icon.clock size={16}/>, onPick: () => insertAt(desc, qfMkWait(vocab), true) },
    { label: "Decision",        hint: "Split into named paths",              accent: "#C8861E", bg: "#FAF1DE", icon: <Icon.network size={16}/>, onPick: () => insertAt(desc, qfMkDecision(vocab), true) },
    { label: "End — outcome",   hint: "Served, left queue, no-show…",      accent: "#475569", bg: "#E7ECF2", icon: <Icon.check size={16}/>, onPick: () => insertAt(desc, qfMkExit(undefined, vocab), true) },
  ];
  const triggerItems = (desc: QInsertDesc) => vocab.flowTriggers.map((tr) => ({
    label: t(tr.labelKey, tr.label), hint: tr.sub, accent: "#14131F", bg: "#EBEDF1", icon: <Icon.users size={15}/>,
    onPick: () => insertAt(desc, qfMkTrigger(tr.id, vocab), true),
  }));

  const inspector = () => {
    if (!sel) return (
      <div>
        <div style={{ padding: 12, background: T.p4, border: `1px solid ${T.p3}`, borderRadius: 8, fontSize: 12, color: T.n1, lineHeight: 1.5 }}>
          <b>{stats.steps}</b> {t('queues.flowBuilder.text9')}<b>{stats.pages}</b> {t('queues.flowBuilder.text10')}<b>{stats.messages}</b> {t('queues.flowBuilder.text11')}<b>{stats.decisions}</b> {t('queues.flowBuilder.text12')}</div>
        <div style={{ marginTop: 14, fontSize: 12.5, color: T.soft, lineHeight: 1.55 }}>{t('queues.flowBuilder.text13')}{" "}<b>+</b> {t('queues.flowBuilder.text14')}{" "}<b>{t('queues.flowBuilder.text15')}</b> {t('queues.flowBuilder.text16')}</div>
      </div>
    );
    const delBtn = (
          <Btn size="sm" variant="secondary" danger icon={<Icon.trash size={13}/>} style={{ marginTop: 8 }} onClick={() => deleteStep(sel.id)}>
            {t("queues.flowBuilder.deleteStep", "Delete step")}</Btn>
    );
    if (sel.kind === "page") {
      const meta = pages.meta(sel.page);
      const qCount = sel.page === "contact" ? ((sel.cfg as { bookingQuestions?: unknown[] }).bookingQuestions ?? []).length : 0;
      return (
        <div>
          <QfHead icon={<Icon.phone size={16}/>} tint={meta.tint} color={meta.color} title={meta.labelKey ? t(meta.labelKey, meta.label) : meta.label} sub={t('queues.flowBuilder.sub')}/>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}><PMini page={sel.page} cfg={sel.cfg} scale={0.5} heroImage={heroImage} /></div>
          <Btn full onClick={() => openPage(sel.id, sel.page)} icon={<Icon.edit size={13}/>} testid="qflow-edit-page-btn">{t('queues.flowBuilder.text18')}</Btn>
          <div style={{ marginTop: 12, padding: 12, background: meta.tint, border: `1px solid ${alpha(meta.color, 30)}`, borderRadius: 8, fontSize: 12, color: T.n1, lineHeight: 1.5 }}>
            {pages.hint ? pages.hint(sel.page, sel.cfg) : meta.sub}
          </div>
          {delBtn}
        </div>
      );
    }
    if (sel.kind === "stage") {
      return <div style={{ fontSize: 12.5, color: T.soft }}>{t('queues.flowBuilder.text24')}</div>;
    }
    if (sel.kind === "qtrigger") {
      const trig = QF_FLOW_TRIGGER_IN(vocab, sel.event);
      return (
        <div>
          <QfHead icon={<Icon.users size={16}/>} tint="#EBEDF1" color="#14131F" title={t('queues.flowBuilder.title2')} sub={t('queues.flowBuilder.sub2')}/>
          <QfField label={t('queues.flowBuilder.label')} hint={trig.sub}>{qfSelect(sel.event, (v) => updateStep(sel.id, { event: v } as any), vocab.flowTriggers.map((tr) => ({ value: tr.id, label: t(tr.labelKey, tr.label) })))}</QfField>
          <div style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.5 }}>{t('queues.flowBuilder.text25')}{" "}<b>{t('queues.flowBuilder.text26')}</b>{t('queues.flowBuilder.text27')}{" "}<b>{t('queues.flowBuilder.text28')}</b>.</div>
          {delBtn}
        </div>
      );
    }
    if (sel.kind === "comms") {
      const m = msgLibrary.find((x) => x.id === sel.messageId) ?? null;
      return (
        <div>
          <QfHead icon={<Icon.msg size={16}/>} tint={T.p4} color={T.p1} title={t('queues.flowBuilder.title3')} sub={t('queues.flowBuilder.sub3')}/>
          <QfField label={t('queues.flowBuilder.label2')}>
            {/* ui-raw: two-line chooser row (label + current selection) — SearchPickerDialog's trigger, not Button's */}
            <button onClick={() => setMsgPick({ stepId: sel.id })} style={chooseBtn}>
              <span style={{ minWidth: 0, textAlign: "left" }}>
                <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: m ? T.n1 : T.soft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m ? m.name : t('queues.flowBuilder.text3')}</span>
                <span style={{ display: "block", fontSize: 11.5, color: T.soft }}>{m ? t('queues.flowBuilder.text29') : t('queues.flowBuilder.text30')}</span>
              </span>
              <span style={{ fontSize: 12, fontWeight: 600, color: T.p1, flexShrink: 0 }}>{m ? t('queues.flowBuilder.text31') : t('queues.flowBuilder.text32')}</span>
            </button>
          </QfField>
          {m ? <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: -8, marginBottom: 14 }}>{m.channels.map((c) => <span key={c} style={{ fontSize: 11.5, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: CH_COLORS[c] ?? T.n5, color: "#fff" }}>{c}</span>)}</div> : null}
          <QfField label={t('queues.flowBuilder.label3')}>{qfSelect(sel.sendWhen, (v) => updateStep(sel.id, { sendWhen: v } as any), vocab.sendWhen)}</QfField>
          {delBtn}
        </div>
      );
    }
    if (sel.kind === "wait") {
      return (
        <div>
          <QfHead icon={<Icon.clock size={16}/>} tint="#EFF1F4" color="#7C8597" title={t('queues.flowBuilder.title4')} sub={t('queues.flowBuilder.sub4')}/>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <QfField label={t('queues.flowBuilder.label4')}><NumberField value={sel.amount} min={1} onChange={(v) => updateStep(sel.id, { amount: Math.max(1, v) } as any)} ariaLabel={t('queues.flowBuilder.ariaLabel2')} style={{ width: "100%", height: 38, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 8, fontSize: 13.5, fontFamily: T.font, boxSizing: "border-box" }}/></QfField>
            <QfField label={t('queues.flowBuilder.label5')}>{qfSelect(sel.unit, (v) => updateStep(sel.id, { unit: v } as any), QF_WAIT_UNITS)}</QfField>
          </div>
          <QfField label={t('queues.flowBuilder.label6')}>{qfSelect(sel.timing, (v) => updateStep(sel.id, { timing: v } as any), vocab.waitTimings.map((o) => ({ value: o.value, label: t(o.labelKey, o.label) })))}</QfField>
          {delBtn}
        </div>
      );
    }
    if (sel.kind === "decision") {
      return (
        <div>
          <QfHead icon={<Icon.network size={16}/>} tint="#FAF1DE" color="#C8861E" title={t('queues.flowBuilder.title5')} sub={t('queues.flowBuilder.sub5')}/>
          <QfField label={t('queues.flowBuilder.label7')}>{qfSelect(sel.condition, (v) => updateStep(sel.id, { condition: v } as any), vocab.decisionConds)}</QfField>
          {/* Named paths (stage 2, 2026-08-31). Two render as the Yes / No pair
              always did; a third is added HERE, deliberately, and named from
              this journey type's vocabulary — same as the condition and the end
              outcome — with a Custom… entry for the operator's own wording. */}
          <QfField label={t("queues.flowBuilder.paths", "Paths")}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {(sel.paths ?? []).map((pth, i) => {
                const st = fpStyle(i);
                /* A label outside the vocabulary IS the custom case — no extra
                   state is stored to remember which mode the picker was in. */
                const custom = vocab.pathLabels.indexOf(pth.label) === -1;
                const setPaths = (next: typeof sel.paths) => updateStep(sel.id, { paths: next } as any);
                const setLabel = (label: string) => setPaths(sel.paths.map((x) => (x.key === pth.key ? { ...x, label } : x)));
                const inner = qfFlatten(pth.steps).length;
                return (
                  <div key={pth.key} style={{ display: "flex", flexDirection: "column", gap: 6, padding: "8px 9px", borderRadius: 8, background: st.tint, border: `1px solid ${st.color}33` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 800, color: st.color, flexShrink: 0, width: 12 }}>{i + 1}</span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        {qfSelect(custom ? FP_CUSTOM : pth.label, (v) => setLabel(v === FP_CUSTOM ? "" : v), vocab.pathLabels.concat([FP_CUSTOM]))}
                      </span>
                      {(sel.paths?.length ?? 0) > 2 ? (
                        <Btn size="sm" variant="secondary" danger iconOnly={<Icon.trash size={13}/>}
                          onClick={() => {
                            const msg = inner
                              ? t("queues.flowBuilder.removePathConfirm", "Delete the \"{label}\" path and the {n} steps in it?").replace("{label}", fpLabel(pth)).replace("{n}", String(inner))
                              : t("queues.flowBuilder.removeEmptyPathConfirm", "Delete the \"{label}\" path?").replace("{label}", fpLabel(pth));
                            if (window.confirm(msg)) setPaths(fpRemovePath(sel.paths, pth.key));
                          }}/>
                      ) : null}
                    </div>
                    {custom ? (
                      <input value={pth.label} onChange={(e) => setLabel(e.target.value)} autoFocus
                        placeholder={t("queues.flowBuilder.namePath", "Name this path")}
                        style={{ width: "100%", height: 32, padding: "0 9px", border: `1px solid ${T.n4}`, borderRadius: 7, background: T.white, color: T.n1, fontSize: 12.5, fontFamily: T.font, outline: "none", boxSizing: "border-box" }}/>
                    ) : null}
                    <span style={{ fontSize: 10.5, color: st.color, opacity: 0.85 }}>{inner} {inner === 1 ? t("queues.flowBuilder.step", "step") : t("queues.flowBuilder.steps", "steps")}</span>
                  </div>
                );
              })}
            </div>
            {(sel.paths?.length ?? 0) < FP_MAX_PATHS ? (
              <Btn size="sm" variant="secondary" icon={<Icon.plus size={13}/>} style={{ marginTop: 8 }}
                onClick={() => updateStep(sel.id, { paths: fpAddPath(sel.paths) } as any)}>
                {t("queues.flowBuilder.addPath", "Add a path")}</Btn>
            ) : (
              <div style={{ fontSize: 10.5, color: T.soft, marginTop: 8 }}>{t("queues.flowBuilder.pathMax", "Three paths is the maximum.")}</div>
            )}
          </QfField>
          <div style={{ fontSize: 11.5, color: T.soft }}>{t('queues.flowBuilder.text35')}{" "}<b>+</b> {t('queues.flowBuilder.text36')}</div>
          {delBtn}
        </div>
      );
    }
    if (sel.kind === "survey") {
      const sv = svLibrary.find((x) => x.id === sel.surveyId) ?? null;
      return (
        <div>
          <QfHead icon={<Icon.csv size={16}/>} tint={SURVEY_TINT} color={SURVEY_COLOR} title={t('queues.flowBuilder.title6')} sub={t('queues.flowBuilder.sub6')}/>
          <QfField label={t('queues.flowBuilder.label8')}>
            {/* ui-raw: two-line chooser row (label + current selection) — SearchPickerDialog's trigger, not Button's */}
            <button onClick={() => setSvPick({ stepId: sel.id })} style={chooseBtn}>
              <span style={{ minWidth: 0, textAlign: "left" }}>
                <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: sv ? T.n1 : T.soft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sv ? sv.name : t('queues.flowBuilder.text6')}</span>
                <span style={{ display: "block", fontSize: 11.5, color: T.soft }}>{sv ? /* TODO(i18n): replace template-literal with t('queues.flowBuilder.text37', { ... }) */`${sv.count} question${sv.count === 1 ? "" : "s"}` : t('queues.flowBuilder.text30')}</span>
              </span>
              <span style={{ fontSize: 12, fontWeight: 600, color: T.p1, flexShrink: 0 }}>{sv ? t('queues.flowBuilder.text31') : t('queues.flowBuilder.text32')}</span>
            </button>
          </QfField>
          <QfField label={t('queues.flowBuilder.label3')}>{qfSelect(sel.sendWhen, (v) => updateStep(sel.id, { sendWhen: v } as any), vocab.sendWhen)}</QfField>
          {delBtn}
        </div>
      );
    }
    const exitSel = sel as { id: string; kind: "exit"; outcome: string; note: string };
    return (
      <div>
        <QfHead icon={<Icon.check size={16}/>} tint="#E7ECF2" color="#475569" title={t('queues.flowBuilder.title7')} sub={t('queues.flowBuilder.sub7')}/>
        <QfField label={t('queues.flowBuilder.label9')}>{qfSelect(exitSel.outcome, (v) => updateStep(sel.id, { outcome: v } as any), vocab.exitOutcomes)}</QfField>
        <QfField label={t('queues.flowBuilder.label10')}>
          <textarea value={exitSel.note} onChange={(e) => updateStep(sel.id, { note: e.target.value } as any)} placeholder={t('queues.flowBuilder.placeholder')} rows={3}
            style={{ width: "100%", padding: 10, border: `1px solid ${T.n4}`, borderRadius: 8, background: T.white, color: T.n1, fontSize: 13, fontFamily: T.font, resize: "vertical", boxSizing: "border-box" }}/>
        </QfField>
        {delBtn}
      </div>
    );
  };

  return (
    <>
      <div style={{ flex: 1, display: "flex", minHeight: 0, height, overflow: "hidden", borderRadius: 12, border: `1px solid ${T.border}`, background: T.white }}>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
          <div style={{ height: 46, display: "flex", alignItems: "center", gap: 8, padding: "0 12px", borderBottom: `1px solid ${T.border}`, background: T.white, flexShrink: 0 }}>
            <span style={{ fontSize: 12, color: T.soft }}>{stats.steps} {t('queues.flowBuilder.text9')}{stats.pages} {t('queues.flowBuilder.text10')}{stats.messages} {t('queues.flowBuilder.text11')}{stats.decisions} {t('queues.flowBuilder.text12')}</span>
            <span style={{ flex: 1 }}/>
            {readOnly ? <span style={{ fontSize: 11.5, fontWeight: 600, color: T.soft, padding: "3px 9px", borderRadius: 999, background: T.n6, border: `1px solid ${T.border}` }}><Icon.eye size={12}/> {t('queues.flowBuilder.text38')}</span> : null}
            <IconBtn size={30} label={t('queues.flowBuilder.label11')} onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2)))}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M5 12h14"/></svg></IconBtn>
            <span style={{ fontSize: 12, color: T.soft, width: 38, textAlign: "center" }}>{Math.round(zoom * 100)}%</span>
            <IconBtn size={30} label={t('queues.flowBuilder.label12')} onClick={() => setZoom((z) => Math.min(1.4, +(z + 0.1).toFixed(2)))}><Icon.plus size={14}/></IconBtn>
          </div>
          <div ref={scrollRef} onClick={() => { if (!readOnly) { setSelectedId(""); setPicker(null); } }}
            style={{ flex: 1, overflow: "auto", background: "#F6F7F9", position: "relative" }}>
            <div style={{ width: layout.width * zoom + 80, height: layout.height * zoom + 80, position: "relative", margin: "0 auto", padding: 40 }}>
              <div style={{ position: "relative", width: layout.width, height: layout.height, transform: `scale(${zoom})`, transformOrigin: "top left" }}>
                <svg width={layout.width} height={layout.height} style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
                  {layout.links.map((l, i) => <path key={i} d={l.d} fill="none" stroke={l.color} strokeWidth={2}/>)}
                  {layout.labels.map((lb, i) => (
                    <g key={"lb" + i}>
                      <rect x={lb.x - lb.w / 2} y={lb.y - 9} width={lb.w} height={18} rx={9} fill={lb.tint}/>
                      <text x={lb.x} y={lb.y + 4} textAnchor="middle" fontSize={10} fontWeight="700" fill={lb.color} fontFamily="inherit">{lb.text}</text>
                    </g>
                  ))}
                  {layout.ends.map((e, i) => e.text ? <text key={"e" + i} x={e.x} y={e.y + 4} textAnchor="middle" fontSize={11} fill="#9AA3B2" fontFamily="inherit">{e.text}</text> : null)}
                </svg>
                {readOnly ? null : layout.slots.map((sl) => (
                  /* ui-raw: insert-step affordance on the flow canvas — no flow-builder family exists (known gap) */
                  <button key={sl.id}
                    title={dragId ? "Drop the step here" : t('queues.flowBuilder.title8')}
                    onClick={(e) => { e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); setPicker({ x: r.left, y: r.bottom + 6, desc: { parentId: sl.ctx.parentId, pathKey: sl.ctx.pathKey, index: sl.index } }); }}
                    /* The same slot serves both gestures: click to add, drop to
                       move. `qfCanMove` refuses a decision dropped inside its own
                       subtree here rather than at the drop, so the cursor says no
                       while there is still time to aim somewhere else. */
                    onDragOver={(e) => {
                      if (!dragId || !e.dataTransfer?.types?.includes(QF_DND)) return;
                      if (!qfCanMove(flow.steps, dragId, sl.ctx.parentId)) { e.dataTransfer.dropEffect = "none"; return; }
                      e.preventDefault(); e.stopPropagation();
                      e.dataTransfer.dropEffect = "move";
                    }}
                    onDrop={(e) => {
                      const id = e.dataTransfer?.getData(QF_DND);
                      if (!id) return;
                      e.preventDefault(); e.stopPropagation();
                      moveTo({ parentId: sl.ctx.parentId, pathKey: sl.ctx.pathKey, index: sl.index }, id);
                    }}
                    /* While a drag is live every slot is a target, so they grow and take
                       the accent — a 26px circle is findable when you know where
                       it is and not when you are dragging past it. Legal targets
                       only: a decision's own branches stay small and grey, which
                       is the refusal shown before the drop rather than after. */
                    style={(() => {
                      const armed = !!dragId && qfCanMove(flow.steps, dragId, sl.ctx.parentId);
                      const size = armed ? 34 : 26;
                      return { position: "absolute", left: sl.x - size / 2, top: sl.y - size / 2, width: size, height: size, borderRadius: 999, border: `1.5px ${armed ? "dashed" : "solid"} ${armed || sl.pulse ? ACCENT : "#CDD3DD"}`, background: armed || sl.pulse ? T.p4 : "#fff", color: armed || sl.pulse ? ACCENT : "#8A93A2", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2, transition: "width 120ms ease, height 120ms ease" } as CSSProperties;
                    })()}>
                    <Icon.plus size={dragId ? 16 : 14}/>
                  </button>
                ))}
                {layout.nodes.map(renderNode)}
              </div>
            </div>
          </div>
        </div>
        {!readOnly && (
          <div style={{ width: 300, borderLeft: `1px solid ${T.border}`, background: T.white, overflow: "auto", padding: 16 }}>
            {inspector()}
          </div>
        )}
      </div>

      {picker ? (
        <>
          <div onClick={() => setPicker(null)} style={{ position: "fixed", inset: 0, zIndex: 250 }}/>
          <div onClick={(e) => e.stopPropagation()} style={{ position: "fixed", left: Math.min(picker.x, window.innerWidth - 270), top: Math.min(picker.y, window.innerHeight - 520), zIndex: 251, width: 262, maxHeight: 500, overflow: "auto", background: T.white, border: `1px solid ${T.border}`, borderRadius: 13, boxShadow: "0 18px 42px -12px rgba(16,24,40,.34)", padding: 7 }}>
            <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: ".06em", color: T.soft, textTransform: "uppercase", padding: "7px 9px 5px" }}>{t('queues.flowBuilder.text39')}</div>
            {insertItems(picker.desc).map((it) => (
              <MediaRow key={it.label} onClick={it.onPick} title={it.label} subtitle={it.hint}
                leading={<span style={{ width: 32, height: 32, borderRadius: 8, background: it.bg, color: it.accent, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{it.icon}</span>}
                style={{ borderRadius: 8 }} />
            ))}
            <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: ".06em", color: T.soft, textTransform: "uppercase", padding: "9px 9px 5px", borderTop: `1px solid ${T.border}`, marginTop: 5 }}>{t('queues.flowBuilder.text40')}</div>
            {triggerItems(picker.desc).map((it) => (
              <MediaRow key={it.label} onClick={it.onPick} title={it.label} subtitle={it.hint}
                leading={<span style={{ width: 32, height: 32, borderRadius: 8, background: it.bg, color: it.accent, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{it.icon}</span>}
                style={{ borderRadius: 8 }} />
            ))}
            <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: ".06em", color: T.soft, textTransform: "uppercase", padding: "9px 9px 5px", borderTop: `1px solid ${T.border}`, marginTop: 5 }}>{t('queues.flowBuilder.text41')}</div>
            {pageItems(picker.desc).map((it) => (
              <MediaRow key={it.label} onClick={it.onPick} leading={<span style={{ width: 32, height: 32, borderRadius: 8, background: it.bg, color: it.accent, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{it.icon}</span>}
                title={<>{it.label}{it.suggested ? <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: T.p1, background: T.p4, borderRadius: 999, padding: "1px 6px" }}>{t('queues.flowBuilder.text42')}</span> : null}</>} subtitle={<>{it.hint}</>}
                style={{ borderRadius: 8 }} />

            ))}
          </div>
        </>
      ) : null}

      {msgPick ? (
        <div onClick={() => setMsgPick(null)} style={{ position: "fixed", inset: 0, zIndex: 250, background: "rgba(20,16,46,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 560, maxWidth: "94vw", maxHeight: "84vh", background: T.white, borderRadius: 14, boxShadow: "0 20px 50px -12px rgba(16,24,40,.4)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: T.n1, margin: 0 }}>{t('queues.flowBuilder.text4')}</h2>
              <IconBtn label={t('queues.flowBuilder.label13')} onClick={() => setMsgPick(null)}><Icon.x size={15}/></IconBtn>
            </div>
            <div style={{ flex: 1, overflow: "auto", padding: "12px 16px 16px", display: "flex", flexDirection: "column", gap: 6 }}>
              {msgPickable.length === 0 ? <div style={{ padding: 24, textAlign: "center", color: T.soft, fontSize: 13 }}>{t('queues.flowBuilder.text43')}{" "}<b>{t('queues.flowBuilder.text44')}</b> {t('queues.flowBuilder.text45')}</div> : msgPickable.map((m) => (
                <MediaRow key={m.id} onClick={() => { updateStep(msgPick.stepId, { messageId: m.id } as any); setMsgPick(null); }} leading={<span style={{ width: 32, height: 32, borderRadius: 7, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.msg size={15}/></span>}
                  title={<>{m.name}</>} subtitle={<>{m.channels.map((c) => <span key={c} style={{ fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 999, background: CH_COLORS[c] ?? T.n5, color: "#fff" }}>{c}</span>)}</>}
                  style={{ borderRadius: 8 }} />

              ))}
            </div>
            <div style={{ padding: "10px 16px", borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "flex-end" }}>
              <Btn variant="secondary" icon={<Icon.external size={13}/>} onClick={() => { setMsgPick(null); nav.go("messaging"); }}>{t('queues.flowBuilder.text46')}</Btn>
            </div>
          </div>
        </div>
      ) : null}

      {svPick ? (
        <div onClick={() => setSvPick(null)} style={{ position: "fixed", inset: 0, zIndex: 250, background: "rgba(20,16,46,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 560, maxWidth: "94vw", maxHeight: "84vh", background: T.white, borderRadius: 14, boxShadow: "0 20px 50px -12px rgba(16,24,40,.4)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: T.n1, margin: 0 }}>{t('queues.flowBuilder.text8')}</h2>
              <IconBtn label={t('queues.flowBuilder.label13')} onClick={() => setSvPick(null)}><Icon.x size={15}/></IconBtn>
            </div>
            <div style={{ flex: 1, overflow: "auto", padding: "12px 16px 16px", display: "flex", flexDirection: "column", gap: 6 }}>
              {svLibrary.length === 0 ? <div style={{ padding: 24, textAlign: "center", color: T.soft, fontSize: 13 }}>{t('queues.flowBuilder.text47')}</div> : svLibrary.map((s) => (
                <MediaRow key={s.id} onClick={() => { updateStep(svPick.stepId, { surveyId: s.id } as any); setSvPick(null); }} leading={<span style={{ width: 32, height: 32, borderRadius: 7, background: SURVEY_TINT, color: SURVEY_COLOR, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.csv size={15}/></span>}
                  title={<>{s.name}</>} subtitle={<>{(s.count === 1 ? t("queues.flowBuilder.questionCountOne", "{n} question") : t("queues.flowBuilder.questionCountOther", "{n} questions")).replace("{n}", String(s.count))}</>}
                  style={{ borderRadius: 8 }} />

              ))}
            </div>
            <div style={{ padding: "10px 16px", borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "flex-end" }}>
              <Btn variant="secondary" icon={<Icon.external size={13}/>} onClick={() => { setSvPick(null); nav.go("surveys"); }}>{t('queues.flowBuilder.text50')}</Btn>
            </div>
          </div>
        </div>
      ) : null}
      {pageEdit && PEditor ? (() => {
        // The editor pages through the WHOLE journey's pages — arrows move between them.
        const entries: QFPageEntry[] = [];
        const walkP = (arr: QFStep[]) => arr.forEach((s) => { if (s.kind === "page") entries.push({ stepId: s.id, page: s.page, cfg: s.cfg }); else if (s.kind === "decision") { (s.paths ?? []).forEach((p) => walkP(p.steps ?? [])); } });
        walkP(flow.steps);
        if (!entries.length) return null;
        return <PEditor entries={entries} startStepId={pageEdit} heroImage={heroImage}
          onDone={(cfgs) => {
            setSteps((s) => Object.entries(cfgs).reduce((acc, [id, cfg]) => qfEdit(acc, id, (x) => ({ ...x, cfg }) as QFStep), s));
            setPageEdit(null);
          }}
          onCancel={() => setPageEdit(null)} />;
      })() : null}
    </>
  );
}
